import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Badge } from '../../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import Chart from '../../../components/ui/Chart';
import {
  Activity, TrendingUp, TrendingDown, Clock, AlertTriangle,
  CheckCircle, Target, BarChart3, LineChart, PieChart,
  RefreshCw, Download, Filter, Search, Eye, EyeOff,
  Zap, Brain, Cpu, Database, Monitor, Settings,
  ArrowUp, ArrowDown, Star, Crown, Shield, UserCheck,
  Timer, Route, Lightning, UserPlus, UserMinus,
  Calendar, Mail, Phone, MessageSquare, MessageCircle,
  HardDrive, Heart, AlertCircle as AlertCircleIcon,
  FileText, FileSpreadsheet, ChevronDown
} from 'lucide-react';
import toast from 'react-hot-toast';

const AIPerformanceMonitoring = () => {
  const [performanceData, setPerformanceData] = useState([]);
  const [modelMetrics, setModelMetrics] = useState([]);
  const [systemHealth, setSystemHealth] = useState({});
  const [selectedTimeRange, setSelectedTimeRange] = useState('24h');
  const [selectedModel, setSelectedModel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(() => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`;
  });

  useEffect(() => {
    // Mock performance data
    const mockPerformanceData = [
      {
        id: 1,
        modelName: "Ticket Classification",
        timestamp: "2025-01-15T14:30:00Z",
        accuracy: 94.2,
        precision: 94.5,
        recall: 93.8,
        f1Score: 94.1,
        processingTime: 45,
        errorRate: 0.8,
        throughput: 1250,
        status: "healthy"
      },
      {
        id: 2,
        modelName: "Sentiment Analysis",
        timestamp: "2025-01-15T14:25:00Z",
        accuracy: 89.7,
        precision: 89.9,
        recall: 89.5,
        f1Score: 89.7,
        processingTime: 32,
        errorRate: 1.2,
        throughput: 2100,
        status: "healthy"
      },
      {
        id: 3,
        modelName: "Auto-Response Generator",
        timestamp: "2025-01-15T14:20:00Z",
        accuracy: 91.5,
        precision: 91.8,
        recall: 91.2,
        f1Score: 91.5,
        processingTime: 78,
        errorRate: 0.5,
        throughput: 890,
        status: "warning"
      },
      {
        id: 4,
        modelName: "Ticket Assignment",
        timestamp: "2025-01-15T14:15:00Z",
        accuracy: 87.3,
        precision: 87.6,
        recall: 87.0,
        f1Score: 87.3,
        processingTime: 56,
        errorRate: 2.1,
        throughput: 1450,
        status: "critical"
      },
      {
        id: 5,
        modelName: "Fraud Detection",
        timestamp: "2025-01-15T14:10:00Z",
        accuracy: 82.1,
        precision: 82.4,
        recall: 81.8,
        f1Score: 82.1,
        processingTime: 67,
        errorRate: 3.2,
        throughput: 650,
        status: "critical"
      }
    ];

    // Mock model metrics
    const mockModelMetrics = [
      {
        modelName: "Ticket Classification",
        totalPredictions: 125000,
        correctPredictions: 117750,
        accuracy: 94.2,
        avgProcessingTime: 45,
        peakProcessingTime: 78,
        errorCount: 1000,
        lastUpdated: "2025-01-15T14:30:00Z"
      },
      {
        modelName: "Sentiment Analysis",
        totalPredictions: 210000,
        correctPredictions: 188370,
        accuracy: 89.7,
        avgProcessingTime: 32,
        peakProcessingTime: 56,
        errorCount: 2520,
        lastUpdated: "2025-01-15T14:25:00Z"
      },
      {
        modelName: "Auto-Response Generator",
        totalPredictions: 89000,
        correctPredictions: 81435,
        accuracy: 91.5,
        avgProcessingTime: 78,
        peakProcessingTime: 120,
        errorCount: 445,
        lastUpdated: "2025-01-15T14:20:00Z"
      },
      {
        modelName: "Ticket Assignment",
        totalPredictions: 145000,
        correctPredictions: 126585,
        accuracy: 87.3,
        avgProcessingTime: 56,
        peakProcessingTime: 89,
        errorCount: 3045,
        lastUpdated: "2025-01-15T14:15:00Z"
      },
      {
        modelName: "Fraud Detection",
        totalPredictions: 65000,
        correctPredictions: 53365,
        accuracy: 82.1,
        avgProcessingTime: 67,
        peakProcessingTime: 95,
        errorCount: 2080,
        lastUpdated: "2025-01-15T14:10:00Z"
      }
    ];

    // Mock system health
    const mockSystemHealth = {
      overallHealth: "healthy",
      cpuUsage: 45.2,
      memoryUsage: 67.8,
      diskUsage: 34.5,
      networkLatency: 12.3,
      activeConnections: 1250,
      errorRate: 1.2,
      uptime: "99.8%",
      lastHealthCheck: "2025-01-15T14:30:00Z"
    };

    setPerformanceData(mockPerformanceData);
    setModelMetrics(mockModelMetrics);
    setSystemHealth(mockSystemHealth);
  }, []);

  const filteredData = performanceData.filter(item => {
    const matchesSearch = item.modelName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesModel = selectedModel === 'all' || item.modelName === selectedModel;
    return matchesSearch && matchesModel;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'critical': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="h-4 w-4" />;
      case 'warning': return <AlertTriangle className="h-4 w-4" />;
      case 'critical': return <AlertTriangle className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update system health with random values
      setSystemHealth(prev => ({
        ...prev,
        cpuUsage: Math.random() * 30 + 40,
        memoryUsage: Math.random() * 30 + 50,
        errorRate: Math.random() * 2
      }));

      // Update last updated timestamp
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const year = now.getFullYear();
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');
      const seconds = String(now.getSeconds()).padStart(2, '0');
      setLastUpdated(`${month}/${day}/${year}, ${hours}:${minutes}:${seconds}`);

      toast.success('Performance data refreshed successfully');
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setRefreshing(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const exportData = {
        performanceData,
        modelMetrics,
        systemHealth,
        exportedAt: new Date().toISOString()
      };

      if (format === 'csv') {
        const csvContent = [
          'Model,Status,Accuracy,Processing Time,Error Rate,Throughput,Last Updated',
          ...performanceData.map(item => 
            `${item.modelName},${item.status},${item.accuracy}%,${item.processingTime}ms,${item.errorRate}%,${item.throughput},${item.timestamp}`
          )
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-performance-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else if (format === 'json') {
        const jsonContent = JSON.stringify(exportData, null, 2);
        const blob = new Blob([jsonContent], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ai-performance-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }

      toast.success(`Data exported as ${format.toUpperCase()} successfully`);
    } catch (error) {
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const getSystemHealthStatus = () => {
    if (!systemHealth.overallHealth) return { status: 'healthy', color: 'green', label: 'Healthy' };
    if (systemHealth.cpuUsage > 85 || systemHealth.memoryUsage > 90 || systemHealth.errorRate > 2) {
      return { status: 'critical', color: 'red', label: 'Critical' };
    } else if (systemHealth.cpuUsage > 75 || systemHealth.memoryUsage > 80 || systemHealth.errorRate > 1) {
      return { status: 'warning', color: 'yellow', label: 'Warning' };
    }
    return { status: 'healthy', color: 'green', label: 'Healthy' };
  };

  const healthStatus = getSystemHealthStatus();

  const chartData = {
    accuracyTrends: [
      { name: '00:00', value: 94.2 },
      { name: '04:00', value: 94.5 },
      { name: '08:00', value: 93.8 },
      { name: '12:00', value: 94.1 },
      { name: '16:00', value: 94.3 },
      { name: '20:00', value: 94.0 }
    ],
    processingTime: [
      { name: 'Classification', value: 45 },
      { name: 'Sentiment', value: 32 },
      { name: 'Auto-Response', value: 78 },
      { name: 'Assignment', value: 56 },
      { name: 'Fraud Detection', value: 67 }
    ],
    errorRates: [
      { name: 'Classification', value: 0.8 },
      { name: 'Sentiment', value: 1.2 },
      { name: 'Auto-Response', value: 0.5 },
      { name: 'Assignment', value: 2.1 },
      { name: 'Fraud Detection', value: 3.2 }
    ]
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Breadcrumb and Last Updated */}
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-gray-500">
          <span className="text-gray-700 font-medium">AI Orchestrator</span>
          <span className="mx-2">/</span>
          <span>Performance Monitoring</span>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {lastUpdated}
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Performance Monitoring</h1>
          <p className="text-gray-600 mt-1 text-sm">Monitor AI model performance and system health</p>
        </div>
        <div className="flex gap-3">
          <Button 
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
          <div className="relative">
            <Button 
              variant="outline"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="flex items-center gap-2 border-gray-300 hover:bg-gray-50"
            >
              <Download className={`h-4 w-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
              <ChevronDown className="h-4 w-4" />
            </Button>
            
            {showExportMenu && (
              <>
                <div 
                  className="fixed inset-0 z-10" 
                  onClick={() => setShowExportMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <div className="py-1">
                    <button
                      onClick={() => handleExport('csv')}
                      disabled={exporting}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileSpreadsheet className="h-4 w-4 text-green-600" />
                      Export as CSV
                    </button>
                    <button
                      onClick={() => handleExport('json')}
                      disabled={exporting}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <FileText className="h-4 w-4 text-blue-600" />
                      Export as JSON
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* System Health Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-6">
        {/* System Health Card */}
        <Card className="bg-gradient-to-br from-pink-50 to-purple-100 border-2 border-pink-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-purple-700 mb-2 uppercase tracking-wide">
                  System Health
                </p>
                <p className="text-4xl font-extrabold text-purple-900 mb-2">
                  {healthStatus.label}
                </p>
                <p className={`text-xs font-medium ${
                  healthStatus.status === 'healthy' 
                    ? 'text-green-600' 
                    : healthStatus.status === 'warning'
                    ? 'text-yellow-600'
                    : 'text-red-600'
                }`}>
                  {healthStatus.status === 'healthy' 
                    ? 'All systems operational' 
                    : healthStatus.status === 'warning'
                    ? 'Warning - Monitor closely'
                    : 'Critical - Immediate attention needed'}
                </p>
              </div>
              <div className="ml-4 p-3 bg-purple-500 rounded-xl shadow-md">
                <Heart className={`h-10 w-10 text-white ${
                  healthStatus.status === 'healthy' ? 'animate-pulse' : ''
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CPU Usage Card */}
        <Card className="bg-gradient-to-br from-gray-50 to-gray-100 border-2 border-gray-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">CPU Usage</p>
                <p className="text-4xl font-extrabold text-gray-900 mb-2">
                  {systemHealth.cpuUsage ? systemHealth.cpuUsage.toFixed(1) : '0.0'}%
                </p>
                <p className="text-xs font-medium text-gray-600">
                  {systemHealth.cpuUsage > 85 
                    ? 'High usage' 
                    : systemHealth.cpuUsage > 75
                    ? 'Moderate usage'
                    : 'Normal usage'}
                </p>
              </div>
              <div className="ml-4 p-3 bg-violet-400 rounded-xl shadow-md">
                <Cpu className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Memory Usage Card */}
        <Card className="bg-gradient-to-br from-emerald-50 to-green-100 border-2 border-green-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-700 mb-2 uppercase tracking-wide">Memory Usage</p>
                <p className="text-4xl font-extrabold text-green-900 mb-2">
                  {systemHealth.memoryUsage ? systemHealth.memoryUsage.toFixed(1) : '0.0'}%
                </p>
                <p className="text-xs font-medium text-green-600">
                  {systemHealth.memoryUsage > 90 
                    ? 'High usage' 
                    : systemHealth.memoryUsage > 80
                    ? 'Moderate usage'
                    : 'Normal usage'}
                </p>
              </div>
              <div className="ml-4 p-3 bg-green-500 rounded-xl shadow-md">
                <HardDrive className="h-10 w-10 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Rate Card */}
        <Card className="bg-gradient-to-br from-amber-50 to-orange-100 border-2 border-amber-200 rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 transform">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-semibold text-orange-700 mb-2 uppercase tracking-wide">
                  Error Rate
                </p>
                <p className="text-4xl font-extrabold text-orange-900 mb-2">
                  {systemHealth.errorRate ? systemHealth.errorRate.toFixed(2) : '0.00'}%
                </p>
                <p className={`text-xs font-medium ${
                  systemHealth.errorRate > 2 
                    ? 'text-red-600'
                    : systemHealth.errorRate > 1
                    ? 'text-yellow-600'
                    : 'text-orange-600'
                }`}>
                  {systemHealth.errorRate > 2 
                    ? 'Needs attention' 
                    : systemHealth.errorRate > 1
                    ? 'Monitor closely'
                    : 'Within threshold'}
                </p>
              </div>
              <div className="ml-4 p-3 bg-orange-500 rounded-xl shadow-md">
                <AlertCircleIcon className={`h-10 w-10 text-white ${
                  systemHealth.errorRate > 1 ? 'animate-pulse' : ''
                }`} />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-6">
        <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              Accuracy Trends (24h)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              type="line"
              data={chartData.accuracyTrends}
              height={300}
              colors={['#3B82F6']}
            />
          </CardContent>
        </Card>

        <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Clock className="h-5 w-5 text-green-600" />
              Processing Time by Model
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Chart
              type="bar"
              data={chartData.processingTime}
              height={300}
              colors={['#10B981']}
            />
          </CardContent>
        </Card>
      </div>

      {/* Model Performance Table */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm mb-6">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <Activity className="h-5 w-5 text-gray-700" />
              Model Performance Metrics
            </CardTitle>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search models..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-64 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <Select value={selectedModel} onValueChange={setSelectedModel}>
                <SelectTrigger className="w-40 border-gray-300">
                  <SelectValue placeholder="Model" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  {modelMetrics.map((model) => (
                    <SelectItem key={model.modelName} value={model.modelName}>
                      {model.modelName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedTimeRange} onValueChange={setSelectedTimeRange}>
                <SelectTrigger className="w-32 border-gray-300">
                  <SelectValue placeholder="Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">Last Hour</SelectItem>
                  <SelectItem value="24h">Last 24h</SelectItem>
                  <SelectItem value="7d">Last 7 days</SelectItem>
                  <SelectItem value="30d">Last 30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Model</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Status</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Accuracy</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Processing Time</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Error Rate</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Throughput</th>
                  <th className="text-left p-4 text-sm font-semibold text-gray-700">Last Updated</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                    <td className="p-4">
                      <div className="font-semibold text-gray-900">{item.modelName}</div>
                    </td>
                    <td className="p-4">
                      <Badge className={`${getStatusColor(item.status)} rounded-full px-2.5 py-0.5 text-xs font-medium`}>
                        <div className="flex items-center gap-1">
                          {getStatusIcon(item.status)}
                          <span className="capitalize">{item.status}</span>
                        </div>
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-gray-900">{item.accuracy}%</span>
                        {item.accuracy >= 90 ? (
                          <TrendingUp className="h-4 w-4 text-green-600" />
                        ) : (
                          <TrendingDown className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span>{item.processingTime}ms</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <AlertTriangle className={`h-4 w-4 ${
                          item.errorRate > 2 ? 'text-red-500' : 'text-gray-400'
                        }`} />
                        <span className={`text-sm ${
                          item.errorRate > 2 ? 'text-red-600 font-semibold' : 'text-gray-600'
                        }`}>
                          {item.errorRate}%
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Activity className="h-4 w-4 text-blue-500" />
                        <span>{item.throughput.toLocaleString()}/h</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="text-sm text-gray-500">
                        {new Date(item.timestamp).toLocaleString('en-GB', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Detailed Model Metrics */}
      <Card className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <BarChart3 className="h-5 w-5 text-gray-700" />
            Detailed Model Metrics
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {modelMetrics.map((model) => (
              <div 
                key={model.modelName} 
                className="bg-white border border-gray-200 rounded-lg p-5 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-200">
                  <h3 className="font-semibold text-base text-gray-900">{model.modelName}</h3>
                  <Badge 
                    variant="outline" 
                    className="rounded-full px-2.5 py-0.5 text-xs font-medium border-gray-300 text-gray-700 bg-gray-50"
                  >
                    {model.accuracy}% accuracy
                  </Badge>
                </div>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Total Predictions:</span>
                    <span className="font-semibold text-gray-900">{model.totalPredictions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Correct Predictions:</span>
                    <span className="font-semibold text-green-600">{model.correctPredictions.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Avg Processing Time:</span>
                    <span className="font-semibold text-gray-900">{model.avgProcessingTime}ms</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Peak Processing Time:</span>
                    <span className="font-semibold text-orange-600">{model.peakProcessingTime}ms</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Error Count:</span>
                    <span className="font-semibold text-red-600">{model.errorCount.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                    <span className="text-gray-500 text-xs">Last Updated:</span>
                    <span className="font-medium text-xs text-gray-600">
                      {new Date(model.lastUpdated).toLocaleDateString('en-GB', { 
                        day: '2-digit', 
                        month: '2-digit', 
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AIPerformanceMonitoring;
